import { useState } from 'react';

const MONTH_OPTIONS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const REVENUE_CATEGORIES = ['Commissions', 'Investment Management', 'Subscription Fees', 'Tax Preparation'];

const SCENARIO_TYPES = [
  { value: 'new-hire-support', label: 'New Hire (Support)' },
  { value: 'new-hire-revenue', label: 'New Hire (Revenue-Generating)' },
  { value: 'one-time-expense', label: 'One-Time Expense' },
  { value: 'recurring-expense', label: 'Recurring Expense Change' },
  { value: 'revenue-change', label: 'Revenue Change' },
];

const inputClass = 'w-full px-3 py-2 text-[13px] border border-gray-300 rounded-[4px] focus:outline-none focus:border-bullogic-blue';
const labelClass = 'block text-[11px] font-bold uppercase tracking-[0.08em] text-navy mb-1';

export default function ScenarioForm({ onAdd, onCancel, editingScenario }) {
  const e = editingScenario;
  const [type, setType] = useState(e?.type || 'new-hire-support');
  const [title, setTitle] = useState(e?.title || '');
  const [monthlySalary, setMonthlySalary] = useState(e?.monthlySalary ? String(e.monthlySalary) : '');
  const [startMonth, setStartMonth] = useState(e?.startMonth || 1);
  const [payrollTaxRate, setPayrollTaxRate] = useState(e?.payrollTaxRate ? String(e.payrollTaxRate * 100) : '10');
  const [monthlyRevenue, setMonthlyRevenue] = useState(e?.monthlyRevenue ? String(e.monthlyRevenue) : '');
  const [revenueStartMonth, setRevenueStartMonth] = useState(e?.revenueStartMonth || 1);
  const [amount, setAmount] = useState(e?.amount ? String(e.amount) : '');
  const [month, setMonth] = useState(e?.month || 1);
  const [monthlyAmount, setMonthlyAmount] = useState(e?.monthlyAmount ? String(Math.abs(e.monthlyAmount)) : '');
  const [category, setCategory] = useState(e?.category || REVENUE_CATEGORIES[0]);

  function handleSubmit(e) {
    e.preventDefault();
    if (!title.trim()) return;

    const base = {
      id: `scenario-${Date.now()}`,
      type,
      active: true,
      title: title.trim(),
    };

    let scenario;
    switch (type) {
      case 'new-hire-support':
        scenario = {
          ...base,
          monthlySalary: parseFloat(monthlySalary) || 0,
          startMonth: parseInt(startMonth),
          payrollTaxRate: (parseFloat(payrollTaxRate) || 0) / 100,
        };
        break;
      case 'new-hire-revenue':
        scenario = {
          ...base,
          monthlySalary: parseFloat(monthlySalary) || 0,
          startMonth: parseInt(startMonth),
          payrollTaxRate: (parseFloat(payrollTaxRate) || 0) / 100,
          monthlyRevenue: parseFloat(monthlyRevenue) || 0,
          revenueStartMonth: parseInt(revenueStartMonth),
        };
        break;
      case 'one-time-expense':
        scenario = {
          ...base,
          amount: parseFloat(amount) || 0,
          month: parseInt(month),
        };
        break;
      case 'recurring-expense':
        scenario = {
          ...base,
          monthlyAmount: -(Math.abs(parseFloat(monthlyAmount) || 0)),
          startMonth: parseInt(startMonth),
        };
        break;
      case 'revenue-change':
        scenario = {
          ...base,
          monthlyAmount: parseFloat(monthlyAmount) || 0,
          startMonth: parseInt(startMonth),
          category,
        };
        break;
      default:
        return;
    }

    onAdd(scenario);
  }

  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5 border border-bullogic-blue/30">
      <h3 className="text-[14px] font-bold text-navy mb-4">{editingScenario ? 'Edit Scenario' : 'Add Scenario'}</h3>
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Type selector */}
        <div>
          <label className={labelClass}>Scenario Type</label>
          <select value={type} onChange={(e) => setType(e.target.value)} className={inputClass}>
            {SCENARIO_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        {/* Title (all types) */}
        <div>
          <label className={labelClass}>Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Junior Advisor, Office Lease"
            className={inputClass}
            required
          />
        </div>

        {/* New Hire fields */}
        {(type === 'new-hire-support' || type === 'new-hire-revenue') && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Monthly Salary</label>
                <input
                  type="number"
                  value={monthlySalary}
                  onChange={(e) => setMonthlySalary(e.target.value)}
                  placeholder="4500"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Payroll Tax Rate (%)</label>
                <input
                  type="number"
                  value={payrollTaxRate}
                  onChange={(e) => setPayrollTaxRate(e.target.value)}
                  placeholder="10"
                  className={inputClass}
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Start Month</label>
              <select value={startMonth} onChange={(e) => setStartMonth(e.target.value)} className={inputClass}>
                {MONTH_OPTIONS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          </>
        )}

        {/* Revenue hire: additional revenue fields */}
        {type === 'new-hire-revenue' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Projected Monthly Revenue</label>
              <input
                type="number"
                value={monthlyRevenue}
                onChange={(e) => setMonthlyRevenue(e.target.value)}
                placeholder="8000"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Revenue Start Month</label>
              <select value={revenueStartMonth} onChange={(e) => setRevenueStartMonth(e.target.value)} className={inputClass}>
                {MONTH_OPTIONS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* One-Time Expense fields */}
        {type === 'one-time-expense' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Amount</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="5000"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Month</label>
              <select value={month} onChange={(e) => setMonth(e.target.value)} className={inputClass}>
                {MONTH_OPTIONS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Recurring Expense fields */}
        {type === 'recurring-expense' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Monthly Cost</label>
              <input
                type="number"
                value={monthlyAmount}
                onChange={(e) => setMonthlyAmount(e.target.value)}
                placeholder="350"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Start Month</label>
              <select value={startMonth} onChange={(e) => setStartMonth(e.target.value)} className={inputClass}>
                {MONTH_OPTIONS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Revenue Change fields */}
        {type === 'revenue-change' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Monthly Revenue Change</label>
                <input
                  type="number"
                  value={monthlyAmount}
                  onChange={(e) => setMonthlyAmount(e.target.value)}
                  placeholder="2000"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Start Month</label>
                <select value={startMonth} onChange={(e) => setStartMonth(e.target.value)} className={inputClass}>
                  {MONTH_OPTIONS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className={labelClass}>Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputClass}>
                {REVENUE_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <button
            type="submit"
            className="px-4 py-2 text-[12px] font-bold text-white bg-bullogic-blue rounded-[4px] hover:bg-bullogic-blue/90"
          >
            {editingScenario ? 'Update Scenario' : 'Add Scenario'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-[12px] font-bold text-[#888] bg-gray-100 rounded-[4px] hover:bg-gray-200"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
